import React, { useState } from "react";
import { Mail, Lock, User, Phone, ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import ModalRegister from "../../components/ModalTermos/ModalTermos";
import { AuthService } from "../../services/authService.js";

const Login = () => {
  const [isSignUp, setIsSignUp] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerError, setRegisterError] = useState("");
  const navigate = useNavigate();

  // Função para alternar entre login e registro limpando os erros
  const toggleSignUp = (value) => {
    setIsSignUp(value);
    // Limpa o erro da seção oposta
    if (value) {
      setLoginError("");
    } else {
      setRegisterError("");
    }
  };

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      let formatted = value.replace(/\D/g, "");
      if (formatted.length <= 2) formatted = `(${formatted}`;
      else if (formatted.length <= 3)
        formatted = `(${formatted.slice(0, 2)}) ${formatted.slice(2)}`;
      else if (formatted.length <= 7)
        formatted = `(${formatted.slice(0, 2)}) ${formatted.slice(
          2,
          3
        )} ${formatted.slice(3)}`;
      else
        formatted = `(${formatted.slice(0, 2)}) ${formatted.slice(
          2,
          3
        )} ${formatted.slice(3, 7)}-${formatted.slice(7, 11)}`;

      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setRegisterError("");

    if (formData.password !== formData.confirmPassword) {
      setRegisterError("As senhas não coincidem!");
      return;
    }
    if (!termsAccepted) {
      setRegisterError("Você precisa aceitar os termos.");
      return;
    }

    setIsLoading(true);

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.replace(/\D/g, ""),
        password: formData.password,
      };

      const response = await AuthService.register(userData);

      navigate("/confirmar-email", {
        state: { email: formData.email },
      });
    } catch (error) {
      setRegisterError(error.response?.data?.message || "Erro ao cadastrar");
      console.error("Erro no cadastro:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);

    try {
      const response = await AuthService.login(loginData);

      if (response && response.token) {
        sessionStorage.setItem("token", response.token);
        sessionStorage.setItem("user", JSON.stringify(response.user));
        if (rememberMe) {
          localStorage.setItem("token", response.token);
          localStorage.setItem("user", JSON.stringify(response.user));
        }
        navigate("/");
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;

        switch (status) {
          case 400:
            setLoginError(responseData.message || "E-mail e senha são obrigatórios");
            break;
          case 401:
            setLoginError(responseData.message || "Usuário ou senha inválidos");
            break;
          case 402:
            navigate("/confirmar-email", {
              state: { email: loginData.email },
            });
            break;
          case 403:
            setLoginError(responseData.message || "E-mail não verificado");
            if (responseData.reason) {
              console.info("Motivo:", responseData.reason);
            }
            break;
          case 500:
            setLoginError("Erro interno do servidor. Tente novamente mais tarde.");
            break;
          default:
            setLoginError("Erro ao fazer login. Tente novamente.");
        }
      } else {
        setLoginError("Erro de conexão. Verifique sua internet e tente novamente.");
      }

      console.error("Erro no login:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const BackClick = () => navigate("/");
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 overflow-hidden">
      <button
        className="absolute top-2 left-4 md:top-4 md:left-4 bg-green-500 p-3 rounded-full text-white hover:bg-green-600 transition duration-300 transform hover:scale-105"
        onClick={BackClick}>
        <ArrowLeft size={20} />
      </button>

      <div className="relative w-full max-w-[900px] mt-14 md:mt-0 mx-4 md:mx-auto h-auto md:h-[600px] bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="md:hidden flex justify-center py-4 bg-black text-white">
          <button
            onClick={() => setIsSignUp(false)}
            className={`px-4 py-2 ${!isSignUp ? "bg-green-500" : ""}`}>
            Entrar
          </button>
          <button
            onClick={() => setIsSignUp(true)}
            className={`px-4 py-2 ${isSignUp ? "bg-green-500" : ""}`}>
            Cadastrar
          </button>
        </div>

        <div className="flex flex-col md:flex-row transition-all duration-700 ease-in-out">
          {/* Login */}
          <div
            className={`w-full md:w-1/2 p-6 md:p-10 ${
              isSignUp ? "hidden md:flex" : "flex"
            } flex-col justify-center items-center`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Bem-vindo de volta
            </h2>
            <p className="text-gray-500 mb-6 md:mb-8">Entre com sua conta</p>

            {loginError && (
              <div className="w-full mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
                {loginError}
              </div>
            )}

            <form className="w-full" onSubmit={handleLoginSubmit}>
              <div className="mb-4 relative">
                <label className="block text-gray-700 mb-1">Email</label>
                <div className="flex items-center">
                  <Mail className="absolute left-3 text-gray-500 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div className="mb-4 relative">
                <label className="block text-gray-700 mb-1">Senha</label>
                <div className="flex items-center">
                  <Lock className="absolute left-3 text-gray-500 w-5 h-5" />
                  <input
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className="hidden peer"
                  />
                  <div className="w-5 h-5 mr-2 flex items-center justify-center rounded border-2 border-gray-300 peer-checked:bg-green-500 peer-checked:border-green-500 transition-all">
                    {rememberMe && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  Lembrar de mim
                </label>

                <Link
                  to="/emailrec"
                  className="transition duration-200 hover:text-green-700 hover:underline">
                  Esqueceu a senha?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-300 flex justify-center items-center"
                disabled={isLoading}>
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processando...
                  </>
                ) : (
                  "Entrar"
                )}
              </button>
            </form>
          </div>

          {/* Cadastro */}
          <div
            className={`w-full md:w-1/2 p-6 md:p-6 ${
              !isSignUp ? "hidden md:flex" : "flex"
            } flex-col justify-center bg-white`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-0 md:mb-0">Criar Conta</h2>
            <p className="text-gray-500 mb-1 md:mb-1">
              Cadastre-se para acessar todos os recursos
            </p>

            {registerError && (
              <div className="w-full mb-1 md:mb-0  p-2 bg-red-100 text-red-700 rounded text-sm">
                {registerError}
              </div>
            )}

            <form className="w-full" onSubmit={handleSignUpSubmit}>
              <div className="mb-2 relative">
                <label className="block text-gray-700 mb-1">
                  Nome Completo
                </label>
                <div className="flex items-center">
                  <User className="absolute left-3 text-gray-500 w-4 h-4 md:w-5 md:h-5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleSignUpChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="João Silva"
                    required
                  />
                </div>
              </div>

              <div className="mb-2 relative">
                <label className="block text-gray-700 mb-1">Email</label>
                <div className="flex items-center">
                  <Mail className="absolute left-3 text-gray-500 w-4 h-4 md:w-5 md:h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleSignUpChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div className="mb-2 relative">
                <label className="block text-gray-700 mb-1">Telefone</label>
                <div className="flex items-center">
                  <Phone className="absolute left-3 text-gray-500 w-4 h-4 md:w-5 md:h-5" />
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleSignUpChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="(99) 9 9999-9999"
                    required
                  />
                </div>
              </div>

              <div className="mb-2 relative">
                <label className="block text-gray-700 mb-1">Senha</label>
                <div className="flex items-center">
                  <Lock className="absolute left-3 text-gray-500 w-4 h-4 md:w-5 md:h-5" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleSignUpChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="mb-2 relative">
                <label className="block text-gray-700 mb-1">
                  Confirmar Senha
                </label>
                <div className="flex items-center">
                  <Lock className="absolute left-3 text-gray-500 w-4 h-4 md:w-5 md:h-5" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleSignUpChange}
                    className="w-full pl-10 md:pl-12 py-2 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-green-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="flex justify-start items-center text-xs md:text-sm mb-3">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  className="hidden peer"
                />
                <div
                  onClick={() => setTermsAccepted(!termsAccepted)}
                  className="w-5 mr-2 h-5 flex items-center justify-center rounded border-2 border-gray-300 peer-checked:bg-green-500 peer-checked:border-green-500 transition-all cursor-pointer">
                  {termsAccepted && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="w-4 h-4 text-white">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <span>
                  Eu concordo com o{" "}
                  <button
                    type="button"
                    onClick={openModal}
                    className="text-green-500 hover:underline">
                    Termos e Condições
                  </button>
                </span>
                <ModalRegister isOpen={isModalOpen} onClose={closeModal} />
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-300"
                disabled={isLoading}>
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processando...
                  </>
                ) : (
                  "Criar Conta"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Desktop Panel Slider */}
        <div
          className={`hidden md:flex absolute top-0 ${
            isSignUp ? "left-1/2" : "left-0"
          } w-1/2 h-full bg-black text-white flex-col justify-center items-center p-10 transition-all duration-700 ease-in-out`}>
          {isSignUp ? (
            <>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-500">
                Olá, Amigo!
              </h2>
              <p className="mb-6 md:mb-8 text-center">
                Ainda não tem uma conta? Cadastre-se agora.
              </p>
              <button
                onClick={() => setIsSignUp(false)}
                className="px-4 md:px-6 py-2 border border-green-500 rounded hover:bg-green-500 hover:text-white hover:border-green-500 transition duration-300">
                Cadastre-se
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-500">
                Bem-vindo de volta!
              </h2>
              <p className="mb-6 md:mb-8 text-center">
                Já tem uma conta? Entre agora.
              </p>
              <button
                onClick={() => setIsSignUp(true)}
                className="px-4 md:px-6 py-2 border border-green-500 rounded hover:bg-green-500 hover:text-white hover:border-green-500 transition duration-300">
                Entrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
